import React, { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ConfigChangeLogListLanguage, GenericAdminFilterLanguage } from '../../../../../i18n'
import {
    ConfigChangeLogDetailChange,
    ConfigChangeLogListItem
} from '../../../../NonReact/Types/ConfigChangeLogListTypes'
import { configChangeLogTelSysOrPartitionDisplay } from './ConfigChangeLogListConstants'
import { Modal } from '../../Molecules/Modal/Modal'
import { DocTopic } from '../../../../React/Common/Constants/Documentation'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../../../React/Common/Utils/HelpPortal'
import { $window } from '../../../../NonReact/common/AkixiWindow'
import { DataTable, type DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'

export type ConfigChangeLogDetailModalProps = {
    /** Entry to display; the host list should only mount this modal when a row is available. */
    readonly row: ConfigChangeLogListItem
    /** Invoked after the dialog transition has finished (clear host open state here). */
    readonly onModalExited?: () => void
}

/**
 * Row model for the entry summary {@link DataTable}; {@code Description} satisfies the shared table contract.
 */
type AuditTrailSummaryRow = {
    key: React.Key
    Description: string
    Item: string
    Value: string
}

type AuditTrailSummaryColumnKey = keyof Pick<AuditTrailSummaryRow, 'Item' | 'Value'>

/**
 * Row model for the change-field {@link DataTable}; {@code Description} satisfies the shared table contract.
 */
type AuditTrailChangeRow = {
    key: React.Key
    Description: string
    Field: string
    OldValue: string
    NewValue: string
}

type AuditTrailChangeColumnKey = keyof Pick<AuditTrailChangeRow, 'Field' | 'OldValue' | 'NewValue'>

/**
 * Read-only audit trail entry dialog using the same {@link Modal} shell as other admin add/change flows.
 *
 * @param props - Row payload and optional exit callback for the host list.
 * @returns Modal tree mounted by the audit trail list when a row is opened.
 */
export const ConfigChangeLogDetailModal: FC<ConfigChangeLogDetailModalProps> = ({
    row,
    onModalExited
}: ConfigChangeLogDetailModalProps): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ConfigChangeLogListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const noneScopeLabel: string = language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_NONE_LABEL)

    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(true)

    const closeModal = useCallback((): void => {
        $window.disableHotKeys = false
        setModalOpen(false)
    }, [])

    const changes: readonly ConfigChangeLogDetailChange[] = row.Changes ?? []

    const summaryRows: AuditTrailSummaryRow[] = useMemo((): AuditTrailSummaryRow[] => {
        const rows: { key: React.Key; label: string; value: string }[] = [
            {
                key: 'datetime',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_DateTime),
                value: row.DateTime ?? EMPTY_STRING
            },
            {
                key: 'description',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Description),
                value: row.Description ?? EMPTY_STRING
            },
            {
                key: 'user',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_User),
                value: row.UserID ?? EMPTY_STRING
            },
            {
                key: 'action',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Action),
                value: row.Action ?? EMPTY_STRING
            },
            {
                key: 'type',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Type),
                value: row.TableName ?? EMPTY_STRING
            },
            {
                key: 'cme',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_CME),
                value: row.CMEnvironmentDesc ?? row.CMEnvironmentID ?? EMPTY_STRING
            },
            {
                key: 'telsys',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_TelSys),
                value: configChangeLogTelSysOrPartitionDisplay(
                    row.TelephoneSystemDescriptions ?? row.TelephoneSystemIDs,
                    noneScopeLabel
                )
            },
            {
                key: 'partition',
                label: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Partition),
                value: configChangeLogTelSysOrPartitionDisplay(
                    row.PartitionDescriptions ?? row.PartitionIDs,
                    noneScopeLabel
                )
            }
        ]

        return rows.map(
            (entry: { key: React.Key; label: string; value: string }): AuditTrailSummaryRow => ({
                key: entry.key,
                Description: entry.label,
                Item: entry.label,
                Value: entry.value
            })
        )
    }, [language, noneScopeLabel, row])

    const summaryColumns: DataTableColumn<AuditTrailSummaryRow, AuditTrailSummaryColumnKey>[] = useMemo(
        (): DataTableColumn<AuditTrailSummaryRow, AuditTrailSummaryColumnKey>[] => [
            {
                field: 'Item',
                key: 'changelog-summary-item',
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Detail_SummaryColumnItem),
                sortable: false
            },
            {
                field: 'Value',
                key: 'changelog-summary-value',
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Detail_SummaryColumnValue),
                sortable: false
            }
        ],
        [language]
    )

    const changeRows: AuditTrailChangeRow[] = useMemo(
        (): AuditTrailChangeRow[] =>
            changes.map((change: ConfigChangeLogDetailChange, idx: number): AuditTrailChangeRow => {
                const fieldDisplay: string = isStringNotEmpty(change.FieldLabel)
                    ? change.FieldLabel
                    : change.FieldName ?? EMPTY_STRING

                return {
                    key: `${change.FieldName ?? 'field'}-${String(idx)}`,
                    Description: fieldDisplay,
                    Field: fieldDisplay,
                    OldValue: change.OldValue ?? EMPTY_STRING,
                    NewValue: change.NewValue ?? EMPTY_STRING
                }
            }),
        [changes]
    )

    const changeColumns: DataTableColumn<AuditTrailChangeRow, AuditTrailChangeColumnKey>[] = useMemo(
        (): DataTableColumn<AuditTrailChangeRow, AuditTrailChangeColumnKey>[] => [
            {
                field: 'Field',
                key: 'changelog-detail-field',
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Detail_Field),
                sortable: false
            },
            {
                field: 'OldValue',
                key: 'changelog-detail-old',
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Detail_OldValue),
                sortable: false
            },
            {
                field: 'NewValue',
                key: 'changelog-detail-new',
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Detail_NewValue),
                sortable: false
            }
        ],
        [language]
    )

    return (
        <div>
            <Modal
                className='config-change-log-detail-modal'
                isOpen={isModalOpen}
                closeOnBackdropClick
                isClosableWithXButton
                isViewOnly
                onOpen={(): void => {
                    setModalHelpTopicID(DocTopic.AUDIT_TRAIL)
                }}
                onClose={closeModal}
                onExited={(): void => {
                    clearModalHelpTopic()
                    onModalExited?.()
                }}
            >
                {isModalOpen ? (
                    <div className='config-change-log-detail-modal-inner'>
                        <div className='modal-title-report-config'>
                            {language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Detail_Title)}
                        </div>
                        <div className='config-change-log-detail-body'>
                            <div className='config-change-log-detail-summary'>
                                <DataTable<AuditTrailSummaryRow, AuditTrailSummaryColumnKey>
                                    permissionName={AdminBackendPermissionNames.AUDIT_TRAIL}
                                    columns={summaryColumns}
                                    data={summaryRows}
                                    toolbarDisabled
                                    paginationDisabled
                                    dense
                                />
                            </div>
                            {!ArrayUtils.isEmpty(changeRows) ? (
                                <div className='config-change-log-detail-changes'>
                                    <DataTable<AuditTrailChangeRow, AuditTrailChangeColumnKey>
                                        permissionName={AdminBackendPermissionNames.AUDIT_TRAIL}
                                        columns={changeColumns}
                                        data={changeRows}
                                        toolbarDisabled
                                        paginationDisabled
                                        dense
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <></>
                )}
            </Modal>
        </div>
    )
}
